import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteValue,
  Button,
  InputAdornment,
  ListItem,
  ListItemText,
  Stack,
  TextField,
} from '@mui/material';
import {
  ChangeEventHandler,
  Dispatch,
  MouseEventHandler,
  RefObject,
  SetStateAction,
  SyntheticEvent,
  UIEvent,
  useRef,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { identity } from 'remeda';
import { AutoCompleteOption, DynamicIcon } from 'src/components';

export const CreateEntityAutocomplete = <T extends AutoCompleteOption>({
  createButtonText,
  filteredEntities,
  isLoading,
  searchPlaceholder,
  isAddEntityOpen,
  setIsAddEntityOpen,
  inputRef,
  handleScroll,
  onAutocompleteChange,
  onCreateButtonClick,
  onAutocompleteTextFieldChange,
}: {
  createButtonText?: string;
  inputRef: RefObject<HTMLElement | null>;
  isAddEntityOpen: boolean;
  setIsAddEntityOpen: Dispatch<SetStateAction<boolean>>;
  filteredEntities: T[];
  isLoading?: boolean;
  searchPlaceholder: string;
  handleScroll: (e: UIEvent) => Promise<void>;
  onAutocompleteChange: (
    event: SyntheticEvent,
    value: AutocompleteValue<T, false, false, false>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>,
  ) => Promise<void>;
  onCreateButtonClick?: MouseEventHandler<HTMLButtonElement>;
  onAutocompleteTextFieldChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}) => {
  const createClientButtonRef = useRef<HTMLButtonElement>(null);
  const [value, setValue] = useState<T | null>(null);

  const { control, reset } = useForm({
    defaultValues: {
      input: '',
    },
  });

  return (
    <Stack
      sx={{
        paddingTop: '0.0625rem',
        height: !isAddEntityOpen ? 0 : 'auto',
        width: !isAddEntityOpen ? 0 : '100%',
        transition: '0.2s width',
        overflow: 'hidden',
      }}
    >
      <Autocomplete
        autoHighlight
        fullWidth
        clearOnBlur
        blurOnSelect
        loading={isLoading}
        value={value}
        // Params defined by MUI
        // eslint-disable-next-line max-params
        onChange={async (event, newValue, reason, details) => {
          setValue(newValue);

          if (!newValue) {
            return;
          }

          setIsAddEntityOpen(false);
          await onAutocompleteChange(event, newValue, reason, details);

          reset();
          setValue(null);
        }}
        renderInput={(params) => (
          <Controller
            name="input"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                {...params}
                placeholder={searchPlaceholder}
                size="small"
                onChange={onAutocompleteTextFieldChange}
                InputProps={{
                  ...params.InputProps,
                  style: {
                    borderRadius: '1rem',
                  },
                  inputRef,
                  startAdornment: (
                    <InputAdornment position="start">
                      <DynamicIcon name="search" />
                    </InputAdornment>
                  ),
                  endAdornment: null,
                }}
              />
            )}
          />
        )}
        options={filteredEntities}
        filterOptions={identity}
        renderOption={(props, option) => (
          <ListItem {...props} key={option.id}>
            <ListItemText primary={option.label} />
          </ListItem>
        )}
        ListboxProps={{
          onScroll: handleScroll,
          sx: {
            maxHeight: '17.5rem',
          },
        }}
        onBlur={({ target, relatedTarget }) => {
          const value = (target as HTMLInputElement).value;

          if (
            !value &&
            (!createClientButtonRef.current || relatedTarget !== createClientButtonRef.current)
          ) {
            setIsAddEntityOpen(false);
          }
        }}
      />
      {!!createButtonText && (
        <Button
          onClick={(event) => {
            onCreateButtonClick?.(event);
            setIsAddEntityOpen(false);
          }}
          ref={createClientButtonRef}
          sx={{
            width: 'fit-content',
            alignSelf: 'end',
          }}
        >
          {createButtonText}
        </Button>
      )}
    </Stack>
  );
};
